import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../types';

// Define types
export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  autoHide?: boolean;
}

export interface ModalState {
  isOpen: boolean;
  type: string | null;
  data: any;
}

export interface UiState {
  notifications: Notification[];
  modal: ModalState;
  isLoading: boolean;
}

// Initial state
const initialState: UiState = {
  notifications: [],
  modal: {
    isOpen: false,
    type: null,
    data: null,
  },
  isLoading: false,
};

// Create the slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    addNotification: {
      reducer: (state, action: PayloadAction<Notification>) => {
        state.notifications.push(action.payload);
      },
      prepare: (notification: Omit<Notification, 'id'>) => ({
        payload: { ...notification, id: nanoid() },
      }),
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
      state.modal = {
        isOpen: true,
        type: action.payload.type,
        data: action.payload.data || null,
      };
    },
    closeModal: (state) => {
      state.modal = {
        isOpen: false,
        type: null,
        data: null,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

// Export actions and reducer
export const {
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  setLoading,
} = uiSlice.actions;
export default uiSlice.reducer;

// Selectors
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectModal = (state: RootState) => state.ui.modal;
export const selectIsLoading = (state: RootState) => state.ui.isLoading;
